import { Injectable } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    Router,
    RouterStateSnapshot,
    CanActivate,
    CanActivateChild,
} from '@angular/router';
import { AuthService } from './auth.service';
import { toggleFlags, UnitService } from './unit.service';
import { UserModel } from '../models/user.model';
import { MfaPreferencesService } from './mfa-preferences.service';

@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate, CanActivateChild {
    constructor(
        private authService: AuthService,
        private unitService: UnitService,
        private router: Router,
        private mfaPreferencesService: MfaPreferencesService
    ) { }

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Promise<boolean> {
        return this.handleRouteGuard(route, state);
    }

    canActivateChild(
        childRoute: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Promise<boolean> {
        return this.handleRouteGuard(childRoute, state);
    }

    private handleRouteGuard(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Promise<boolean> {
        return new Promise((resolve) => {
            let loadingSub = this.authService.isLoading$.subscribe(
                async (x) => {
                    if (x) return;

                    const currentUser = this.authService.currentUserValue;
                    let routeName: string | null = null;
                    let currentRoute: ActivatedRouteSnapshot | null = route;

                    while (currentRoute) {
                        if (currentRoute.params['routeName']) {
                            routeName = currentRoute.params['routeName'];
                            break;
                        }
                        currentRoute = currentRoute.parent;
                    }
                    const isGracePeriod =
                        this.calculateGracePeriod(currentUser);
                    const isTryingToAccessMFA =
                        state.url.includes('/settings/mfa');

                    const factors =
                        await this.mfaPreferencesService.checkMfaStatus();

                    // if (
                    //     !isGracePeriod &&
                    //     !isTryingToAccessMFA &&
                    //     factors.length < 1
                    // ) {
                    //     if (routeName) {
                    //         this.router.navigate([
                    //             `/u/${routeName}/settings/mfa`,
                    //         ]);
                    //     } else {
                    //         this.router.navigate(['/auth/login']);
                    //     }
                    //     setTimeout(() => loadingSub.unsubscribe(), 200);
                    //     resolve(false);
                    //     return;
                    // }



                    if (currentUser && routeName) {
                        let location = currentUser.data.find(
                            (x) => x.salesLocationRoute === routeName
                        );
                        if (location) {
                            let result = await this.unitService.getAppLocations(
                                location.salesLocationRoute
                            );
                            location.salesLocationApps = result;
                            this.authService.selectSalesLocation(location);
                            const targetFeatures =
                                state.url.includes('catering') ? 'catering'
                                    : state.url.includes('stamp-cards') ? 'stamp-cards'
                                        : state.url.includes('/signage') ? 'signage'
                                            : state.url.includes('/invoice/') ? 'corporateCustomers'
                                                : /^\/u\/[^/]+\/staff(\/|$)/.test(state.url) ? 'staff'
                                                    : state.url.includes('weeklymenusV2') ? 'weeklyMenuOrdering'
                                                        : '';

                            if (targetFeatures) {
                                try {
                                    await this.unitService.setToggleFlags(routeName);
                                    const hasCatering = toggleFlags && toggleFlags.hasCatering === true
                                    const hasStampCard = toggleFlags && toggleFlags.hasStampCard === true
                                    const hasSignage = toggleFlags && toggleFlags.hasSignage === true
                                    const hasCorporateCustomer = toggleFlags && toggleFlags.hasCorporateCustomer === true
                                    const hasStaff = toggleFlags && toggleFlags.hasStaff === true
                                    const hasWeeklyMenuOrdering = toggleFlags && toggleFlags.hasWeeklyMenuOrdering === true

                                    if (targetFeatures == 'catering' && hasCatering) {
                                        resolve(true);
                                        return;
                                    } else if (targetFeatures == 'stamp-cards' && hasStampCard) {
                                        resolve(true);
                                        return;
                                    } else if (targetFeatures == 'signage' && hasSignage) {
                                        resolve(true);
                                        return;
                                    } else if (targetFeatures == 'corporateCustomers' && hasCorporateCustomer) {
                                        resolve(true);
                                        return;
                                    } else if (targetFeatures == 'staff' && hasStaff) {
                                        resolve(true);
                                        return;
                                    } else if (targetFeatures == 'weeklyMenuOrdering' && hasWeeklyMenuOrdering) {
                                        resolve(true);
                                        return;
                                    } else {
                                        this.router.navigate(['/auth/login']);
                                        setTimeout(() => loadingSub.unsubscribe(), 200);
                                        resolve(false);
                                        return;
                                    }
                                } catch (err) {
                                    this.router.navigate(['/auth/login']);
                                    setTimeout(() => loadingSub.unsubscribe(), 200);
                                    resolve(false);
                                    return;
                                }
                            }
                            resolve(true);
                            return;
                        } else {
                            this.router.navigate(['/auth/login'], {
                                queryParams: {},
                            });
                            setTimeout(() => loadingSub.unsubscribe(), 200);
                            return;
                        }
                    }
                    this.router.navigate(['/auth/login'], {
                        queryParams: {},
                    });
                    setTimeout(() => loadingSub.unsubscribe(), 200);
                    resolve(false);
                }
            );
        });
    }

    private calculateGracePeriod(currentUser: UserModel): boolean {
        const creationTime = currentUser?.metadata?.creationTime;
        if (!creationTime) return false;

        const creationDate = new Date(creationTime);
        const cutoffDate = new Date('2025-08-19');
        let graceEndDate: Date;

        if (creationDate > cutoffDate) {
            graceEndDate = new Date(creationDate);
            graceEndDate.setDate(graceEndDate.getDate() + 14);
        } else {
            graceEndDate = new Date(cutoffDate);
            graceEndDate.setMonth(graceEndDate.getMonth() + 6);
            // graceEndDate.setDate(graceEndDate.getDate() + 1); // For testing
        }

        const now = new Date();

        return now < graceEndDate;
    }
}
